import React, { useState, useMemo } from 'react';
import SITE2SCOPE_LOGO_REVERSED from './site2scopeLogoReversed';
import { WORKFLOW_LABELS } from './dataModel';
import { APP_VERSION, buildStamp } from './appVersion';
import { checkForUpdate } from './swRegistration';
import { showToast } from './Toast';
import Icon from './Icons';

// ─── Estimator colour palette ─────────────────────────────────────────────────
const AVATAR_COLORS = [
  ['#394553', '#d4a8db'], ['#5B2D82', '#f5eaf8'], ['#E8871B', '#fde3c0'],
  ['#5a1e66', '#e8d0ef'], ['#c9650a', '#fde3c0'], ['#3d1247', '#cfa8d8'],
  ['#4d2b00', '#fbc98a'], ['#1a1a1c', '#e2e8f0'],
];

function avatarColor(name) {
  if (!name) return ['#64748b', '#e2e8f0'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function EstimatorAvatar({ name, size = 32 }) {
  const [bg, fg] = avatarColor(name);
  return (
    <div className="pl-avatar" style={{
      width: size, height: size, background: bg, color: fg,
      fontSize: size * 0.38, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, flexShrink: 0, userSelect: 'none',
    }}>
      {initials(name)}
    </div>
  );
}

// ─── Project card with swipe-to-reveal actions ───────────────────────────────
function ProjectCard({ p, onSelect, onDelete, onArchive, onUnarchive, isArchived, onArchiveStart, onEdit, openSwipeId, setOpenSwipeId, canEdit }) {
  const internal = p.internalCount || 0;
  const external = p.externalCount || 0;
  const total    = p.areaCount || 0;
  const photos   = p.photoCount || 0;
  const thumb    = p.heroThumbnailUrl || p.heroPhotoUrl || null;

  const SWIPE_WIDTH = 200; // px of action buttons revealed
  const THRESHOLD   = 48;  // min drag to trigger open
  const touchStart  = React.useRef(null);
  const [dragX, setDragX]   = React.useState(0);  // live drag offset (negative = left)
  const [settled, setSettled] = React.useState(false); // true = snapped open, animate off
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef(null);

  // Close desktop menu on outside click
  React.useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const isOpen = openSwipeId === p.firestoreId;

  // Close if another card opens
  React.useEffect(() => {
    if (!isOpen) { setDragX(0); setSettled(false); }
  }, [isOpen]);

  const areaLabel = internal || external
    ? [internal && `${internal} int`, external && `${external} ext`].filter(Boolean).join(' · ')
    : 'No areas';

  const onTouchStart = (e) => {
    if (window.innerWidth >= 768) return;
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, moved: false };
  };

  const onTouchMove = (e) => {
    if (!touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    // If vertical movement dominates, don't intercept scroll
    if (!touchStart.current.moved && Math.abs(dy) > Math.abs(dx)) { touchStart.current = null; return; }
    touchStart.current.moved = true;
    e.preventDefault();
    if (isOpen) {
      // Already open — allow dragging back right
      const raw = -SWIPE_WIDTH + dx;
      setDragX(Math.min(0, Math.max(-SWIPE_WIDTH, raw)));
    } else {
      // Swipe left only
      if (dx > 0) return;
      setDragX(Math.max(-SWIPE_WIDTH, dx));
    }
  };

  const onTouchEnd = () => {
    if (!touchStart.current?.moved) { touchStart.current = null; return; }
    touchStart.current = null;
    if (isOpen) {
      // If dragged back far enough, close
      if (dragX > -SWIPE_WIDTH + THRESHOLD) { setOpenSwipeId(null); }
      else { setDragX(-SWIPE_WIDTH); }
    } else {
      if (dragX < -THRESHOLD) { setDragX(-SWIPE_WIDTH); setSettled(true); setOpenSwipeId(p.firestoreId); }
      else { setDragX(0); }
    }
  };

  const handleCardClick = () => {
    if (isOpen) { setOpenSwipeId(null); return; }
    if (!isArchived) onSelect(p);
  };

  const translateX = isOpen && settled ? 0 : dragX;
  const cardWidth = isOpen && settled ? `calc(100% - ${SWIPE_WIDTH}px)` : '100%';
  const useTransition = settled || dragX === 0;

  return (
    <div className="pl-card-wrap">
      {/* Revealed action buttons — sit behind the card */}
      <div className="pl-swipe-actions">
        {isArchived ? (
          <>
            <button className="pl-swa-btn pl-swa-unarchive" onClick={() => { setOpenSwipeId(null); onUnarchive(p.firestoreId); }}><Icon name="refresh" size={13} /> Restore</button>
            {canEdit && <button className="pl-swa-btn pl-swa-delete"   onClick={() => { setOpenSwipeId(null); onDelete(p.firestoreId); }}><Icon name="trash" size={13} /> Delete</button>}
          </>
        ) : (
          <>
            <button className="pl-swa-btn pl-swa-edit" onClick={() => { setOpenSwipeId(null); onEdit(p); }}><Icon name="edit" size={13} /> Edit</button>
            <button className="pl-swa-btn pl-swa-archive" onClick={() => { setOpenSwipeId(null); onArchiveStart(p.firestoreId); }}><Icon name="archive" size={13} /> Archive</button>
            {canEdit && <button className="pl-swa-btn pl-swa-delete"  onClick={() => { setOpenSwipeId(null); onDelete(p.firestoreId); }}><Icon name="trash" size={13} /> Delete</button>}
          </>
        )}
      </div>

      {/* Card — slides left on swipe, shrinks when settled to expose buttons */}
      <div
        className={`pl-card ${isArchived ? 'pl-card-archived' : ''}`}
        style={{
          transform: translateX ? `translateX(${translateX}px)` : 'none',
          width: cardWidth,
          transition: useTransition ? 'transform 0.22s ease, width 0.22s ease' : 'none',
        }}
        onClick={handleCardClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Thumbnail */}
        <div className="pl-thumb">
          {thumb
            ? <img src={thumb} alt="" />
            : <div className="pl-thumb-empty">
                <span>{internal > 0 ? '🏠' : external > 0 ? '🏗️' : '📋'}</span>
              </div>
          }
        </div>

        {/* Main content */}
        <div className="pl-card-main">
          <div className="pl-card-top-row">
            <div className="pl-card-title">
              {p.jobNumber && <span className="pl-job-num">#{p.jobNumber}</span>}
              {p.jobNumber && ' — '}
              {p.projectName || p.clientName || 'Unnamed Project'}
            </div>
            {p.estimatorName && <EstimatorAvatar name={p.estimatorName} />}
          </div>

          {p.clientName && p.projectName && (
            <div className="pl-card-ref">{p.clientName}</div>
          )}
          {p.address && <div className="pl-card-address">📍 {p.address}</div>}

          <div className="pl-card-bottom-row">
            <div className="pl-card-stats">
              <span>{areaLabel}</span>
              {photos > 0 && <span>📷 {photos}</span>}
              {p.date && <span>{p.date.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1')}</span>}
            </div>

            <div className="pl-card-right">
              {(() => {
                const wf = p.workflow || 'measuring';
                if (wf === 'measuring') {
                  const withSurfaces = p.areasWithSurfacesCount || 0;
                  const fullyMeasured = p.measuredAreaCount || 0;
                  if (fullyMeasured >= total && total > 0) return <span className="pl-wf-badge" style={{ background: '#059669' }}>✅ All Measured</span>;
                  if (withSurfaces > 0) return <span className="pl-wf-badge" style={{ background: '#5B2D82' }}>📐 Measuring</span>;
                  return total > 0 ? <span className="pl-wf-badge" style={{ background: '#94a3b8' }}>📐 Ready</span> : null;
                }
                if (wf === 'ready_to_assess' || wf === 'assessing') {
                  const assessed = p.assessedAreaCount || 0;
                  if (assessed >= total && total > 0) return <span className="pl-wf-badge" style={{ background: '#059669' }}>✅ All Assessed</span>;
                  if (assessed > 0) return <span className="pl-wf-badge" style={{ background: '#d97706' }}>📋 Assessing</span>;
                  return <span className="pl-wf-badge" style={{ background: '#E8871B' }}>📋 Ready to Assess</span>;
                }
                const wfMeta = WORKFLOW_LABELS[wf];
                return wfMeta ? (
                  <span className="pl-wf-badge" style={{ background: wfMeta.color }}>{wfMeta.icon} {wfMeta.label}</span>
                ) : null;
              })()}
              {total > 0 && (() => {
                const inAssess = p.workflow === 'ready_to_assess' || p.workflow === 'assessing' || p.workflow === 'assessed' || p.workflow === 'comparing';
                const progressCount = inAssess ? (p.assessedAreaCount || 0) : (p.measuredAreaCount || 0);
                const progressPct = total > 0 ? Math.round((progressCount / total) * 100) : 0;
                return (
                  <div className="pl-progress-wrap">
                    <div className="pl-progress-track">
                      <div className="pl-progress-fill" style={{ width: `${progressPct}%`, background: progressPct >= 100 ? '#059669' : undefined }} />
                    </div>
                    <span className="pl-progress-label">{progressCount}/{total}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Chevron + desktop ⋯ menu */}
        <div className="pl-card-actions">
          <div className="pl-desktop-menu" ref={menuRef}>
            <button className="pl-menu-trigger" onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }} title="Options" aria-label="Project options" aria-expanded={showMenu}>⋯</button>
            {showMenu && (
              <div className="pl-menu-dropdown" onClick={e => e.stopPropagation()}>
                {isArchived ? (
                  <>
                    <button onClick={() => { setShowMenu(false); onUnarchive(p.firestoreId); }}><Icon name="refresh" size={13} /> Restore</button>
                    {canEdit && <button className="pl-menu-danger" onClick={() => { setShowMenu(false); onDelete(p.firestoreId); }}><Icon name="trash" size={13} /> Delete</button>}
                    {!canEdit && <button disabled style={{opacity:0.4}}><Icon name="trash" size={13} /> Delete (owner only)</button>}
                  </>
                ) : (
                  <>
                    <button onClick={() => { setShowMenu(false); onEdit(p); }}><Icon name="edit" size={13} /> Edit</button>
                    <button onClick={() => { setShowMenu(false); onArchiveStart(p.firestoreId); }}><Icon name="archive" size={13} /> Archive</button>
                    {canEdit && <button className="pl-menu-danger" onClick={() => { setShowMenu(false); onDelete(p.firestoreId); }}><Icon name="trash" size={13} /> Delete</button>}
                  </>
                )}
              </div>
            )}
          </div>
          <span className="pl-chevron">›</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main list ────────────────────────────────────────────────────────────────
function ProjectsList({ projects, showArchived, onToggleArchived, onSelect, onDelete, onArchive, onUnarchive, onCreate, onUpdate, currentUser, onSignOut, onUpdateDisplayName }) {
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy]       = useState('date');
  const [showMine, setShowMine]   = useState(false);
  const [search, setSearch]       = useState('');
  const [archiveToast, setArchiveToast] = useState(null);
  const [openSwipeId, setOpenSwipeId]   = useState(null);
  const [editProject, setEditProject]   = useState(null); // project being edited
  const [showProfile, setShowProfile]   = useState(false);
  const [editingName, setEditingName]   = useState(false);
  const [nameDraft, setNameDraft]       = useState('');
  const [updateCheckResult, setUpdateCheckResult] = useState(null);
  const archiveTimer = React.useRef(null);
  const [form, setForm] = useState({
    jobNumber: '', clientName: '', address: '', projectName: '',
    estimatorName: '', bdmName: '', projectCreator: '',
    date: new Date().toISOString().slice(0, 10)
  });
  const [editForm, setEditForm] = useState({});

  const handleCheckUpdate = async () => {
    setUpdateCheckResult('checking');
    const result = await checkForUpdate();
    setUpdateCheckResult(result);
    if (result === 'up-to-date') {
      setTimeout(() => setUpdateCheckResult(null), 3000);
    }
  };

  const handleEditOpen = (p) => {
    setEditProject(p);
    setEditForm({
      jobNumber: p.jobNumber || '',
      clientName: p.clientName || '',
      address: p.address || '',
      projectName: p.projectName || '',
      estimatorName: p.estimatorName || '',
      bdmName: p.bdmName || '',
      date: p.date || new Date().toISOString().slice(0, 10),
    });
  };

  const handleEditSave = () => {
    if (!editForm.jobNumber?.trim() && !editForm.clientName?.trim() && !editForm.projectName?.trim()) return;
    if (onUpdate) onUpdate(editProject.firestoreId, editForm);
    setEditProject(null);
  };

  const handleArchiveStart = (firestoreId) => {
    clearTimeout(archiveTimer.current);
    setArchiveToast(firestoreId);
    archiveTimer.current = setTimeout(() => {
      setArchiveToast(null);
      onArchive(firestoreId);
    }, 5000);
  };
  const undoArchive = () => {
    clearTimeout(archiveTimer.current);
    setArchiveToast(null);
  };

  const resetForm = () => {
    const roles = currentUser?.roles || [];
    const name  = currentUser?.displayName || '';
    setForm({
      jobNumber: '', clientName: '', address: '', projectName: '',
      estimatorName: (roles.includes('measurer') || roles.includes('estimator')) ? name : '',
      bdmName:       (roles.includes('bdm'))                                     ? name : '',
      projectCreator: name,
      date: new Date().toISOString().slice(0, 10),
    });
  };

  const handleCreate = () => {
    if (!form.jobNumber.trim() && !form.clientName.trim() && !form.projectName.trim()) {
      showToast('Please enter a job number or project name', 'warning'); return;
    }
    onCreate(form);
    setShowModal(false);
    resetForm();
  };

  // Sort + filter + search
  const displayed = useMemo(() => {
    const WF_ORDER = { measuring: 0, ready_to_assess: 1, assessing: 2, assessed: 3, comparing: 4, approved: 5 };
    let list = [...projects];
    // My Projects toggle
    if (showMine && currentUser?.uid) list = list.filter(p => (p.contributors || [p.userId]).includes(currentUser.uid));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        (p.jobNumber || '').toLowerCase().includes(q) ||
        (p.projectName || '').toLowerCase().includes(q) ||
        (p.clientName || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      if (sortBy === 'name')   return (a.clientName || '').localeCompare(b.clientName || '');
      if (sortBy === 'status') {
        const diff = (WF_ORDER[a.workflow] ?? 0) - (WF_ORDER[b.workflow] ?? 0);
        if (diff !== 0) return diff;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      }
      // date — newest first
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
    return list;
  }, [projects, sortBy, showMine, search, currentUser]);

  return (
    <div className="s2s-screen">
      <div className="s2s-header">
        <img src={SITE2SCOPE_LOGO_REVERSED} alt="Site2Scope" className="pl-header-logo-full" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {currentUser && (
            <button className="pl-user-chip" onClick={() => { setShowProfile(v => !v); setEditingName(false); }}>
              <span className="pl-user-initial">{(currentUser.displayName || currentUser.email || '?')[0].toUpperCase()}</span>
            </button>
          )}
          <button className="s2s-btn-new" onClick={() => { resetForm(); setShowModal(true); }}>＋ New Project</button>
        </div>
      </div>

      {/* ── Profile panel — dropdown on desktop, bottom sheet on mobile ── */}
      {showProfile && currentUser && (
        <div className={window.innerWidth >= 768 ? "pd-profile-dropdown-wrap" : "pd-mobile-edit-overlay"} onClick={() => setShowProfile(false)}>
          <div className={window.innerWidth >= 768 ? "pd-profile-dropdown" : "pd-mobile-edit-sheet"} onClick={e => e.stopPropagation()} style={window.innerWidth < 768 ? { maxHeight: 320 } : undefined}>
            {window.innerWidth < 768 && (
              <div className="pd-mobile-edit-header">
                <span className="pd-mobile-edit-title">👤 Profile</span>
                <button className="pd-mobile-edit-close" onClick={() => setShowProfile(false)} aria-label="Close">✕</button>
              </div>
            )}
            <div style={{ padding: window.innerWidth >= 768 ? '14px 16px' : '16px 20px' }}>
              <div className="pl-profile-header" style={{ marginBottom: 14 }}>
                <EstimatorAvatar name={currentUser.displayName || currentUser.email} size={44} />
                <div className="pl-profile-info">
                  {editingName ? (
                    <div className="pl-profile-name-edit">
                      <input autoFocus value={nameDraft}
                        onChange={e => setNameDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && nameDraft.trim()) { onUpdateDisplayName?.(nameDraft.trim()); setEditingName(false); }
                          if (e.key === 'Escape') setEditingName(false);
                        }} />
                      <button className="pl-profile-name-save" onClick={() => {
                        if (nameDraft.trim()) { onUpdateDisplayName?.(nameDraft.trim()); }
                        setEditingName(false);
                      }}>✓</button>
                    </div>
                  ) : (
                    <div className="pl-profile-name" onClick={() => { setNameDraft(currentUser.displayName || ''); setEditingName(true); }}>
                      {currentUser.displayName || currentUser.email || '—'}
                      <span className="pl-profile-edit-icon">✏️</span>
                    </div>
                  )}
                  <div className="pl-profile-role">
                    {currentUser.role === 'assessor' ? '📋 Assessor' : '📐 Measurer'}
                  </div>
                  {currentUser.email && <div className="pl-profile-email">{currentUser.email}</div>}
                </div>
              </div>
              {onSignOut && (
                <button className="pl-profile-signout" onClick={() => { setShowProfile(false); onSignOut(); }}>
                  Sign Out →
                </button>
              )}
              <div className="pl-version-section">
                <div className="pl-version-label">
                  Site2Scope v{APP_VERSION}
                  <span className="pl-version-date">{buildStamp()}</span>
                </div>
                <button
                  className="pl-check-update-btn"
                  onClick={handleCheckUpdate}
                  disabled={updateCheckResult === 'checking'}
                >
                  {updateCheckResult === 'checking' ? 'Checking\u2026'
                    : updateCheckResult === 'up-to-date' ? '\u2713 Up to date'
                    : updateCheckResult === 'update-available' ? '\u2B06 Update available!'
                    : updateCheckResult === 'offline' ? 'Offline \u2014 can\'t check'
                    : updateCheckResult === 'error' ? 'Check failed \u2014 try again'
                    : 'Check for updates'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pl-content">
        {/* Archive toggle — always visible */}
        <div className="pl-archive-bar">
          <button className={`pl-archive-toggle ${showArchived ? 'active' : ''}`} onClick={onToggleArchived}>
            {showArchived ? <><Icon name="arrow-left" size={13} /> Active Projects</> : <><Icon name="archive" size={13} /> View Archived</>}
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Icon name={showArchived ? 'archive' : 'folder'} size={64} />
            </div>
            <div className="empty-state-title">{showArchived ? 'No archived projects' : 'No projects yet'}</div>
            <div className="empty-state-desc">{showArchived
              ? 'Archive a project from the main list to see it here'
              : <>Click <strong>+ New Project</strong> to start a site visit</>
            }</div>
          </div>
        ) : (
          <>
            {/* ── Search bar ── */}
            <div className="pl-search-bar">
              <input
                className="pl-search-input"
                type="text"
                placeholder="Search job number or project name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className="pl-search-clear" onClick={() => setSearch('')} aria-label="Clear search">✕</button>}
            </div>

            {/* ── Sort / filter bar ── */}
            <div className="pl-toolbar">
              <div className="pl-sort-group">
                <span className="pl-toolbar-label">Sort</span>
                {[
                  { key: 'date',   label: 'Newest' },
                  { key: 'name',   label: 'Client'  },
                  { key: 'status', label: 'Status'  },
                ].map(s => (
                  <button
                    key={s.key}
                    className={`pl-sort-btn ${sortBy === s.key ? 'active' : ''}`}
                    onClick={() => setSortBy(s.key)}
                  >{s.label}</button>
                ))}
              </div>

              <button
                className={`pl-sort-btn ${showMine ? 'active' : ''}`}
                onClick={() => setShowMine(v => !v)}
                title="Show only your projects"
              >
                👤 My Projects
              </button>

              <span className="pl-count">{displayed.length} project{displayed.length !== 1 ? 's' : ''}</span>
            </div>

            {/* ── Card list ── */}
            <div className="pl-list">
              {displayed.length === 0 && search.trim() && (
                <div className="empty-state" style={{padding:'32px 20px'}}>
                  <div className="empty-state-icon"><Icon name="search" size={48} /></div>
                  <div className="empty-state-desc">No projects matching "<strong>{search}</strong>"</div>
                </div>
              )}
              {displayed.map(p => (
                <ProjectCard key={p.firestoreId} p={p} onSelect={onSelect} onDelete={onDelete} onArchive={onArchive} onUnarchive={onUnarchive} isArchived={showArchived} onArchiveStart={handleArchiveStart} onEdit={handleEditOpen} openSwipeId={openSwipeId} setOpenSwipeId={setOpenSwipeId} canEdit={!currentUser || !(p.contributors?.length) || (p.contributors || []).includes(currentUser.uid)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── New project modal ── */}
      {showModal && (
        <div className="s2s-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="s2s-modal" onClick={e => e.stopPropagation()}>
            <div className="s2s-modal-header">
              <h2>New Project</h2>
              <button className="s2s-modal-close" onClick={() => { setShowModal(false); resetForm(); }} aria-label="Close">✕</button>
            </div>
            <div className="modal-grid-2">
              <div className="s2s-field">
                <label>Job Number *</label>
                <input autoFocus placeholder="e.g. 2024-0142" value={form.jobNumber}
                  onChange={e => setForm({ ...form, jobNumber: e.target.value })} />
              </div>
              <div className="s2s-field">
                <label>Date</label>
                <input type="date" value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div className="s2s-field">
              <label>Client Name *</label>
              <input placeholder="e.g. Brisbane City Council" value={form.clientName}
                onChange={e => setForm({ ...form, clientName: e.target.value })} />
            </div>
            <div className="s2s-field">
              <label>Site Address</label>
              <input placeholder="e.g. 123 Main St, Brisbane" value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="s2s-field">
              <label>Project Reference <span className="s2s-optional">optional</span></label>
              <input placeholder="e.g. Greenfields Primary — Block B" value={form.projectName}
                onChange={e => setForm({ ...form, projectName: e.target.value })} />
            </div>
            <div className="s2s-field">
              <label>Estimator Name</label>
              <input placeholder="Your name" value={form.estimatorName}
                onChange={e => setForm({ ...form, estimatorName: e.target.value })} />
            </div>
            <div className="s2s-field">
              <label>BDM Name <span className="s2s-optional">optional</span></label>
              <input placeholder="Business development manager" value={form.bdmName}
                onChange={e => setForm({ ...form, bdmName: e.target.value })} />
            </div>
            <div className="s2s-field">
              <label>Created by</label>
              <input value={form.projectCreator} readOnly
                style={{ background: 'var(--surface-2, #f3f4f6)', color: 'var(--text-2, #6b7280)', cursor: 'default' }} />
            </div>
            <button className="s2s-btn-primary" onClick={handleCreate}>Create Project →</button>
          </div>
        </div>
      )}

      {/* ── Edit project modal ── */}
      {editProject && (
        <div className="s2s-modal-overlay" onClick={() => setEditProject(null)}>
          <div className="s2s-modal" onClick={e => e.stopPropagation()}>
            <div className="s2s-modal-header">
              <h2>Edit Project</h2>
              <button className="s2s-modal-close" onClick={() => setEditProject(null)} aria-label="Close">✕</button>
            </div>
            <div className="modal-grid-2">
              <div className="s2s-field"><label>Job Number</label><input value={editForm.jobNumber} onChange={e=>setEditForm(f=>({...f,jobNumber:e.target.value}))} /></div>
              <div className="s2s-field"><label>Date</label><input type="date" value={editForm.date} onChange={e=>setEditForm(f=>({...f,date:e.target.value}))} /></div>
              <div className="s2s-field modal-col-2"><label>Client Name</label><input value={editForm.clientName} onChange={e=>setEditForm(f=>({...f,clientName:e.target.value}))} /></div>
              <div className="s2s-field modal-col-2"><label>Project Name</label><input value={editForm.projectName} onChange={e=>setEditForm(f=>({...f,projectName:e.target.value}))} /></div>
              <div className="s2s-field modal-col-2"><label>Address</label><input value={editForm.address} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))} /></div>
              <div className="s2s-field"><label>Estimator</label><input value={editForm.estimatorName} onChange={e=>setEditForm(f=>({...f,estimatorName:e.target.value}))} /></div>
              <div className="s2s-field"><label>BDM</label><input value={editForm.bdmName} onChange={e=>setEditForm(f=>({...f,bdmName:e.target.value}))} /></div>
            </div>
            <div className="modal-actions">
              <button className="s2s-btn-create" onClick={handleEditSave}>Save Changes</button>
              <button className="s2s-btn-cancel" onClick={() => setEditProject(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive undo toast (fixed, always visible) ── */}
      {archiveToast && (
        <div className="pd-archive-toast">
          <span>📦 Archiving project…</span>
          <button className="pd-archive-undo" onClick={undoArchive}>Undo</button>
        </div>
      )}
    </div>
  );
}

export default ProjectsList;
